import {IMainView, MainViewConfigs, MainViewType} from "app/services/mainViewVisibility/types";
import {IScheduleState} from "app/reducers/schedule/types";
import {loadStatus} from "shared-types/WidgetTypes";

const NS = 'MainViewVisibilityService';


const floatingViewRS = 'floatingView-runSheet';
const floatingViewFPV2 = 'floatingView-floorplanV2';
const floatingViewTLV1 = 'floatingView-timelineV1';
const floatingViewTLV2 = 'floatingView-timelineV2';

class MainViewVisibilityService {
  private floorplanVersion = 2; // Always use V2 now
  private timelineVersion = 1;
  private currentView: MainViewType;
  private viewConfigs: IMainView[] = [];

  private floorplanView: IMainView;
  private timelineView: IMainView;
  private runsheetView: IMainView;

  private closedScheduleRetryCount = 0;

  init(enableNewFloorPlan: boolean, enableNewTimeline = false) {
    // Floorplan V1 is removed, always use V2
    this.floorplanVersion = 2;
    this.timelineVersion = enableNewTimeline ? 2 : 1;
    this.setViews();
  }

  private setViews() {
    const viewConfigs: IMainView[] = [MainViewConfigs.runSheet];
    this.runsheetView = MainViewConfigs.runSheet;

    // Always use floorplan V2
    this.floorplanView = MainViewConfigs.floorplanV2;
    viewConfigs.unshift(MainViewConfigs.floorplanV2);

    if (this.timelineVersion === 1) {
      this.timelineView = MainViewConfigs.timelineV1;
      viewConfigs.unshift(MainViewConfigs.timelineV1);
    } else if (this.timelineVersion === 2) {
      this.timelineView = MainViewConfigs.timelineV2;
      viewConfigs.unshift(MainViewConfigs.timelineV2);
    }

    this.viewConfigs = viewConfigs;
  }

  /**
   * Called when changing dates in Floorplan. Polls until the new schedule has fully loaded, then checks if
   * it is closed for that day.
   */
  setIsClosedByRedux(
    getScheduleReducer: () => IScheduleState,
    loadingCB: () => void,
    setClosedCB: (isClosed: boolean) => void
  ): boolean {
    const {selectedSchedule, selectedScheduleLoadingStatus} = getScheduleReducer();

    if (!selectedSchedule || selectedScheduleLoadingStatus === loadStatus.loading) {
      loadingCB();

      this.closedScheduleRetryCount += 1;
      if (this.closedScheduleRetryCount >= 10) {
        this.closedScheduleRetryCount = 0;
        return true;
      }

      console.log(NS, `Schedule not loaded yet. Retrying count ${this.closedScheduleRetryCount}`);
      setTimeout(() => {
        const isScheduleLoaded = this.setIsClosedByRedux(getScheduleReducer, loadingCB, setClosedCB);
        if (isScheduleLoaded) {
          this.closedScheduleRetryCount = 0;
          const {selectedSchedule} = getScheduleReducer();
          const isClosed = !selectedSchedule.isOpen || !selectedSchedule.services?.length
          setClosedCB(isClosed);
        }
      }, 200);
      return false;
    }

    return true;
  }


  getViewByType(type: MainViewType): IMainView {
    return this.viewConfigs.find(v => v.view === type);
  }


  // just used for AngularJS HTML
  getAngularJSHTMLConfig(currentView?: MainViewType, resetAllOpened = false, setBodyCssClasses = true) {

    if (currentView) {
      this.currentView = currentView;
    }

    if (resetAllOpened) {
      this.viewConfigs.forEach(vc => vc.opened = false);
    }

    const viewConfig = this.getViewByType(this.currentView);
    if (viewConfig) {
      viewConfig.opened = true;
    }

    if (setBodyCssClasses && currentView && viewConfig.opened) {
      this.setBodyCssClassName(viewConfig.view);
    }

    return {
      isVisible: {
        floorplanV2: this.isFloorplanV2Visible(),
        timelineV1: this.isTimelineV1Visible(),
        timelineV2: this.isTimelineV2Visible(),
        runSheet: this.isRunSheetVisible(),
      },
      isOpened: {
        floorplanV2: this.isFloorplanV2Opened(),
        timelineV1: this.isTimelineV1Opened(),
        timelineV2: this.isTimelineV2Opened(),
        runSheet: this.isRunSheetOpened(),
      },
      viewConfigs: this.viewConfigs,
      currentView: this.currentView
    }
  }

  clearBodyCssClassNames() {
    document.body.classList.remove(floatingViewRS, floatingViewFPV2, floatingViewTLV1, floatingViewTLV2);
  }

  private setBodyCssClassName(view: MainViewType) {
    this.clearBodyCssClassNames();

    let cssClassName: string;
    switch (view) {
      case MainViewType.RunSheet:
        cssClassName = floatingViewRS;
        break;

      case MainViewType.TimelineV1:
        cssClassName = floatingViewTLV1;
        break;

      case MainViewType.TimelineV2:
        cssClassName = floatingViewTLV2;
        break;

      case MainViewType.FloorplanV2:
        cssClassName = floatingViewFPV2;
        break;
    }
    if (cssClassName) {
      document.body.classList.add(cssClassName);
    }
  }


  // ----- FP v2

  isFloorplanV2Visible(): boolean {
    return this.floorplanVersion === 2 && this.currentView === MainViewType.FloorplanV2;
  }

  private isFloorplanV2Opened(): boolean {
    return this.floorplanVersion === 2 && this.floorplanView.opened;
  }

  // ----- TL v1

  isTimelineV1Visible(): boolean {
    return this.timelineVersion === 1 && this.currentView === MainViewType.TimelineV1;
  }

  private isTimelineV1Opened(): boolean {
    if (this.isFloorplanV2Visible()) {
      /**
       * this will force timeline v1 to be removed from the DOM whenever floorplan v2 is open, which boosts
       * performance dramatically on large busy venues (> 100 daily bookings) when using tablets
       */
      return false;
    }
    return this.timelineVersion === 1 && this.timelineView.opened;
  }

  // ----- TL v2

  private isTimelineV2Visible(): boolean {
    return this.timelineVersion === 2 && this.currentView === MainViewType.TimelineV2;
  }

  private isTimelineV2Opened(): boolean {
    return this.timelineVersion === 2 && this.timelineView.opened;
  }

  // ----- Runsheet

  private isRunSheetVisible(): boolean {
    return this.currentView === MainViewType.RunSheet;
  }

  private isRunSheetOpened(): boolean {
    if (this.isFloorplanV2Visible()) {
      /**
       * this will force timeline v1 to be removed from the DOM whenever floorplan v2 is open, which boosts
       * performance dramatically on large busy venues (> 100 daily bookings) when using tablets
       */
      return false;
    }
    return this.runsheetView.opened;
  }
}

const instance = new MainViewVisibilityService();
export default instance as MainViewVisibilityService;
